import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { NAV_LINKS, NAV_SECTION_IDS, ROUTES, SECTION_IDS } from '../../core/constants/navigation';
import { MAIN_CONTENT_ID } from '../../global/config/layout';
import BrandLogo from '../../common/components/BrandLogo/BrandLogo';
import Button from '../../common/components/Button/Button';
import { IconArrowRight, IconClose, IconMenu } from '../../common/icons';
import { useActiveSection } from './hooks/useActiveSection';
import { useIsScrolled } from './hooks/useIsScrolled';

import './styles/header.css';

const NO_SECTIONS = [];
const SCROLL_OFFSET = 8;

export default function Header() {
    const { pathname } = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const isHome = pathname === ROUTES.HOME;
    const activeId = useActiveSection(isHome ? NAV_SECTION_IDS : NO_SECTIONS);
    const isScrolled = useIsScrolled(SCROLL_OFFSET);

    useEffect(() => {
        if (!isMenuOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsMenuOpen(false);
        };
        document.body.classList.add('is-scroll-locked');
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.classList.remove('is-scroll-locked');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen]);

    const closeMenu = () => setIsMenuOpen(false);

    const headerClasses = [
        'header',
        isScrolled ? 'header--scrolled' : '',
        isMenuOpen ? 'header--menu-open' : '',
    ].join(' ').trim();

    return (
        <header className={headerClasses}>
            <a href={`#${MAIN_CONTENT_ID}`} className="skip-link">Saltar al contenido</a>
            <div className="header__inner layout-container">
                <Link to={ROUTES.HOME} className="header__brand" onClick={closeMenu}>
                    <BrandLogo />
                    <span className="visually-hidden">, ir al inicio</span>
                </Link>

                <nav id="main-navigation" className="header__nav" aria-label="Principal">
                    <ul className="header__menu">
                        {NAV_LINKS.map(link => (
                            <li key={link.id} className="header__item">
                                <Link
                                    to={{ pathname: ROUTES.HOME, hash: `#${link.id}` }}
                                    className={`header__link ${activeId === link.id ? 'header__link--active' : ''}`.trim()}
                                    aria-current={activeId === link.id ? 'true' : undefined}
                                    onClick={closeMenu}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    <Button
                        to={{ pathname: ROUTES.HOME, hash: `#${SECTION_IDS.CONTACT}` }}
                        icon={<IconArrowRight />}
                        className="header__cta"
                        onClick={closeMenu}
                    >
                        Solicita tu asesoría
                    </Button>
                </nav>

                <button
                    type="button"
                    className="header__toggle"
                    onClick={() => setIsMenuOpen(isOpen => !isOpen)}
                    aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                    aria-expanded={isMenuOpen}
                    aria-controls="main-navigation"
                >
                    {isMenuOpen ? <IconClose /> : <IconMenu />}
                </button>
            </div>
        </header>
    );
}
